/**
 * Client-safe half of register rum-web-vitals-1101 (no server imports, so the reporter chunk stays tiny).
 * The reporter beacons when the tab is hidden; a metric that web-vitals finalises AFTER that (INP and CLS are
 * reported on hide too, in no fixed order against our listener) must still go out, and an unchanged one must
 * not be sent twice. So each flush carries only the metrics that changed since the last successful send.
 */
export type Vitals = Partial<Record<'TTFB' | 'FCP' | 'LCP' | 'INP' | 'CLS', number>>

export function pendingVitals(current: Vitals, sent: Vitals): Vitals | null {
  const out: Vitals = {}
  let any = false
  for (const k of Object.keys(current) as (keyof Vitals)[]) {
    const v = current[k]
    if (typeof v === 'number' && Number.isFinite(v) && sent[k] !== v) { out[k] = v; any = true }
  }
  return any ? out : null
}

/**
 * CLS ATTRIBUTION (register rum-cls-attribution-1103): a selector-like name for the element behind the LARGEST
 * layout shift, so a real-visitor CLS we cannot reproduce (signed-in /chart, /compare) still names its element.
 * Tag, #id and up to 3 classes, NO text content, so nothing a person typed or reads can leave the page. A text
 * node is named by its parent element.
 */
type ShiftNode = { nodeType?: number; tagName?: string; id?: string; className?: unknown; parentElement?: ShiftNode | null }
export function describeShiftNode(node: ShiftNode | null | undefined): string | null {
  const el = node && node.nodeType === 3 ? node.parentElement : node
  if (!el || typeof el.tagName !== 'string') return null
  let out = el.tagName.toLowerCase()
  if (typeof el.id === 'string' && /^[A-Za-z0-9_-]{1,40}$/.test(el.id)) out += '#' + el.id
  // Same charset the server accepts (webVitals.ts SHIFT_TARGET): a class it would refuse is skipped here rather
  // than sent, or one odd class (a Tailwind [&::...] variant) would cost the whole attribution.
  const cls = typeof el.className === 'string' ? el.className.split(/\s+/).filter((c) => /^[A-Za-z0-9_:/[\]().%!-]{1,48}$/.test(c)).slice(0, 3) : []
  for (const c of cls) out += '.' + c
  return out.slice(0, 160)
}

/**
 * PRIOR LOADS (register rum-proxy-flood-rows-in-web-vitals-1103): how many hard loads this browser made here before
 * this one, from localStorage, capped. The rotating-proxy crawler that floods /compare runs real Chrome and sends
 * RUM beacons, but starts EVERY load with empty storage (60 of 60 of its page views in one burst had 60 distinct
 * localStorage session ids), so its rows always read 0 and a returning visitor's never do. A read can then judge on
 * prior_loads >= 1 (returning visitors, flood-immune) beside the whole table. Null = storage unusable, which is
 * NOT 0: a browser that cannot persist the count would otherwise look like the flood on every load.
 */
export const RUM_LOADS_KEY = 'dt_rum_loads'
export const PRIOR_LOADS_CAP = 50
export function takePriorLoads(storage: Pick<Storage, 'getItem' | 'setItem'> | null | undefined): number | null {
  try {
    if (!storage) return null
    const n = Math.floor(Number(storage.getItem(RUM_LOADS_KEY) || '0'))
    const prior = Number.isFinite(n) && n > 0 ? Math.min(n, PRIOR_LOADS_CAP) : 0
    storage.setItem(RUM_LOADS_KEY, String(Math.min(prior + 1, PRIOR_LOADS_CAP)))
    return prior
  } catch { return null }
}

/**
 * THEME (register light-mode-audience-unmeasured-1104): the theme the visitor actually SAW, read from the class
 * next-themes puts on <html>. Nothing else on the site records it, so a light-mode sweep could not be sized
 * against the people who use light. Read at flush time, after hydration: the served HTML carries no theme class
 * (next-themes applies it client-side), so a read at first paint would be null for everyone. Null = no class.
 */
type ClassRoot = { classList?: { contains(c: string): boolean } } | null | undefined
export function resolvedTheme(root: ClassRoot): 'light' | 'dark' | null {
  try {
    if (!root?.classList) return null
    if (root.classList.contains('light')) return 'light'
    if (root.classList.contains('dark')) return 'dark'
    return null
  } catch { return null }
}

export function deviceClass(width: number): 'mobile' | 'tablet' | 'desktop' | undefined {
  if (!Number.isFinite(width) || width <= 0) return undefined
  return width < 768 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop'
}
